import { useCallback, useEffect, useRef, useState } from "react"

/* A row that scrolls sideways with its scrollbar hidden is a row nobody knows is there.
 *
 * The game's top bar is the case that named this: five chips in about 150px of a 390px phone, with
 * `overflow-x: auto` and `scrollbar-width: none`. Three of the five were off the right edge —
 * including the action budget, which is the number that decides what a player may do this round —
 * and nothing on screen said so. Every width sweep called the bar clean, correctly: the content
 * never leaves its box, the box is simply a window onto more than it can show.
 *
 * A fixed fade on the edge is the usual answer and it is wrong here, because the same row *does*
 * fit from about 900px up, and softening the last chip's edge when there is nothing beyond it is a
 * cue that lies. So the element publishes what it actually has: `data-scroll` is "start", "end",
 * "both" or absent, and the stylesheet masks the edges it names.
 *
 * Watched three ways because each misses the others: `scroll` for the player swiping, a
 * `ResizeObserver` for the window changing and for the content changing width (a chip whose number
 * grows a digit), and one measurement on mount, since neither fires until something moves.
 *
 * `axis` is "x" by default, which is the row above. "y" is the same question asked of a column —
 * the game roster, whose four class plates are taller than the share of the dock they live in and
 * whose scrollbar is hidden for the same reason this row's is. "start"/"end" then mean top and
 * bottom, and the stylesheet decides which way to point the fade.
 */
export const useScrollEdges = ({ axis = "x" } = {}) => {
  const ref = useRef(null)
  const [edges, setEdges] = useState(null)

  const measure = useCallback(() => {
    const el = ref.current
    if (!el) return
    const vertical = axis === "y"
    const offset = vertical ? el.scrollTop : el.scrollLeft
    const view = vertical ? el.clientHeight : el.clientWidth
    const total = vertical ? el.scrollHeight : el.scrollWidth
    // A pixel of slack: fractional layout sizes make `scrollWidth` and `clientWidth` disagree by
    // less than one on rows that fit perfectly, which would pin a fade to a row nobody can scroll.
    const start = offset > 1
    const end = offset + view < total - 1
    setEdges(start && end ? "both" : start ? "start" : end ? "end" : null)
  }, [axis])

  useEffect(() => {
    const el = ref.current
    if (!el) return undefined
    measure()
    el.addEventListener("scroll", measure, { passive: true })
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    // The row's own size is one half of the question; the content's is the other, and a flex row
    // does not resize when a child does. A grid of cards does not either — and the roster's
    // content changes when a seat is eliminated and its chip goes, not only when the window moves.
    Array.from(el.children).forEach((child) => observer.observe(child))
    return () => {
      el.removeEventListener("scroll", measure)
      observer.disconnect()
    }
  }, [measure])

  return [ref, edges]
}

export default useScrollEdges
